import './style.css';
import { ProjectPicker } from './ui/ProjectPicker';
import { DropZone } from './ui/DropZone';
import { FileListView } from './ui/FileListView';
import { UploadedFilesView } from './ui/UploadedFilesView';
import { UploadManager } from './core/UploadManager';
import { deleteSession, listSessions } from './core/UploadSessionStore';
import { formatBytes, formatSpeed } from './core/format';
import { escapeHtml, makeButton, requireElement } from './ui/dom';
import { PersistedSession, ScannedFile } from './core/types';

let currentProject: string | null = null;

const manager = new UploadManager({
  onChange: (item) => {
    renderSummary();
    if (item.status === 'completed') void uploadedFilesView.refresh();
  },
});

const fileListView = new FileListView((id, action) => {
  if (action === 'pause') manager.pause(id);
  if (action === 'resume') manager.resume(id);
  if (action === 'cancel') void manager.cancel(id);
});

const uploadedFilesView = new UploadedFilesView();

const dropZone = new DropZone({
  isEnabled: () => currentProject !== null,
  onFilesAdded: (files) => void handleFilesAdded(files),
});

const projectPicker = new ProjectPicker({
  onProjectSelected: (projectName) => {
    currentProject = projectName;
    dropZone.setEnabled(projectName !== null);
    manager.resetBatch();
    void refreshResumablePanel();
    void uploadedFilesView.setProject(projectName);
  },
});

const retryFailedBtn = requireElement<HTMLButtonElement>('retry-failed-btn');
const resumableBtn = requireElement<HTMLButtonElement>('resumable-btn');
const resumablePanel = requireElement<HTMLElement>('resumable-panel');
const summaryProgressFill = requireElement<HTMLElement>('summary-progress-fill');
const summaryCount = requireElement('summary-count');
const summarySpeed = requireElement('summary-speed');
const summaryPercent = requireElement('summary-percent');

retryFailedBtn.addEventListener('click', () => manager.retryFailed());
resumableBtn.addEventListener('click', () => {
  resumablePanel.classList.toggle('hidden');
});

async function handleFilesAdded(files: ScannedFile[]): Promise<void> {
  if (!currentProject) return;
  await manager.addFiles(currentProject, files);
  await refreshResumablePanel();
}

function renderSummary(): void {
  const items = manager.getItems();
  for (const item of items) fileListView.upsert(item);

  const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0);
  const uploadedBytes = items.reduce((sum, item) => sum + Math.min(item.bytesUploaded, item.file.size), 0);
  const percent = totalBytes === 0 ? 0 : Math.round((uploadedBytes / totalBytes) * 100);
  const activeSpeed = items
    .filter((item) => item.status === 'uploading')
    .reduce((sum, item) => sum + item.speedBps, 0);

  summaryProgressFill.style.width = `${percent}%`;
  summaryPercent.textContent = `${percent}%`;
  summaryCount.textContent = `${items.length}개 파일 (${formatBytes(uploadedBytes)} / ${formatBytes(totalBytes)})`;
  summarySpeed.textContent = activeSpeed > 0 ? formatSpeed(activeSpeed) : '';

  const hasFailed = items.some((item) => item.status === 'failed');
  retryFailedBtn.classList.toggle('hidden', !hasFailed);
}

async function refreshResumablePanel(): Promise<void> {
  if (!currentProject) {
    resumableBtn.classList.add('hidden');
    resumablePanel.classList.add('hidden');
    return;
  }

  const activeUploadIds = new Set(manager.getItems().map((item) => item.uploadId).filter(Boolean));
  const sessions = (await listSessions()).filter(
    (session) => session.projectName === currentProject && !activeUploadIds.has(session.uploadId),
  );

  if (sessions.length === 0) {
    resumableBtn.classList.add('hidden');
    resumablePanel.classList.add('hidden');
    resumablePanel.innerHTML = '';
    return;
  }

  resumableBtn.classList.remove('hidden');
  resumableBtn.textContent = `이어할 업로드 ${sessions.length}건`;
  renderResumablePanel(sessions);
}

function renderResumablePanel(sessions: PersistedSession[]): void {
  resumablePanel.innerHTML = '';

  const heading = document.createElement('h2');
  heading.textContent = '이전에 중단된 업로드 — 같은 파일을 다시 드래그하거나 선택하면 이어받습니다';
  resumablePanel.appendChild(heading);

  const list = document.createElement('ul');
  for (const session of sessions) {
    const li = document.createElement('li');
    const progressChunks = `${session.uploadedChunkIndexes.length}/${session.totalChunks} 청크 완료`;
    li.innerHTML = `
      <span class="item-info">${escapeHtml(session.relativePath)}
        <span class="item-meta">(${formatBytes(session.fileSize)} · ${progressChunks})</span>
      </span>
    `;
    li.appendChild(
      makeButton('기록 삭제', () => {
        void deleteSession(session.fileKey).then(() => refreshResumablePanel());
      }),
    );
    list.appendChild(li);
  }
  resumablePanel.appendChild(list);
}

void projectPicker.load();
